import { t, type ErrorCodeMapperEntry } from "./types.js";

/** Error codes 19000-19999. One thousand-block per file: a duplicate number here can only
 *  collide within this file, which is what makes the split safer than one shared object. */
export const reviewsErrors = {
  // 19xxx is a fresh block for the Reviews module (packages/server/reviews/) — split off
  // SOMETHING_WENT_WRONG (9000). Several sites in this module paired real, specific
  // `errorMessage`s ("no permission...", "Insufficient permissions...") with the generic code
  // instead of the already-existing `INSUFFICIENT_PERMISSION` — those were fixed to use that
  // existing code directly rather than minting duplicates; only genuinely new concepts got a new
  // code here.
  //
  // "Review not found" / "The review does not exist" / "The default review does not exist" — the
  // review row itself could not be located.
  REVIEW_NOT_FOUND: {
    errorCode: 19001,
    errorMessage: t("The review does not exist"),
    isDynamic: false,
  },
  // `initiateReview`/`createReview`-adjacent lookups: the review FLOW template or its space could
  // not be resolved. Distinct from REVIEW_NOT_FOUND (an actual review instance).
  REVIEW_FLOW_SPACE_NOT_FOUND: {
    errorCode: 19002,
    errorMessage: t("The review flow/space does not exist"),
    isDynamic: false,
  },
  // `updateFilesInReview`: the review is Closed/Revoked, or the current stage's `allowFileUpdate`
  // flag is off.
  REVIEW_FILE_UPDATE_NOT_ALLOWED: {
    errorCode: 19003,
    errorMessage: t("The file(s) can not be updated"),
    isDynamic: false,
  },
  // `addResourcesToReview`/similar: the review could not be found, or is Closed/Revoked, so
  // resources cannot be added/changed. The message says "added" even at the remove-context call site.
  REVIEW_RESOURCE_ADD_BLOCKED: {
    errorCode: 19004,
    errorMessage: t("The resources could not be added"),
    isDynamic: false,
  },
  // `canUserMarkAReview`: the session user is not a reviewer for the current approval stage at all
  // (not merely lacking a generic permission — a structural "you're not on this review" state).
  REVIEW_USER_NOT_REVIEWER: {
    errorCode: 19005,
    errorMessage: t("User is not an authentic reviewer"),
    isDynamic: false,
  },
  // A review level's clearance condition (required file statuses) is not yet satisfied.
  REVIEW_LEVEL_CONDITIONS_NOT_MET: {
    errorCode: 19006,
    errorMessage: t("The level can not be cleared until file statuses satisfy level conditions"),
    isDynamic: false,
  },
  // `canUserMarkAReview`: the approval stage has already been revoked.
  REVIEW_REVOKED: {
    errorCode: 19007,
    errorMessage: t("The review has been revoked"),
    isDynamic: false,
  },
  // `getStatusFromDefaultReviewFlow`-adjacent: no default review flow exists for this space.
  // Distinct from REVIEW_FLOW_SPACE_NOT_FOUND (a named/explicit flow lookup).
  REVIEW_FLOW_DEFAULT_NOT_FOUND: {
    errorCode: 19008,
    errorMessage: t("The default review flow does not exist"),
    isDynamic: false,
  },
  // `canUserMarkAReview`: the review's overall status is not "Open" (Draft/Closed/Revoked), so no
  // response can be marked right now.
  REVIEW_NOT_ACTIVE: {
    errorCode: 19009,
    errorMessage: t("The review is not active"),
    isDynamic: false,
  },
  // `notifyUsersInLevel`: every reviewer in the current level has already responded or is deleted —
  // nothing to notify.
  REVIEW_NO_REVIEWER_TO_NOTIFY: {
    errorCode: 19010,
    errorMessage: t("There is no reviewer to notify"),
    isDynamic: false,
  },
  // `notifyUserInReview`: the review is Draft/Closed/Revoked, or the named level is not the
  // in-progress stage — notifying is not meaningful right now. 2 call sites, same concept.
  REVIEW_NOTIFY_BLOCKED: {
    errorCode: 19011,
    errorMessage: t("The user can not be notified"),
    isDynamic: false,
  },
  // `notifyUserInReview`: the named reviewerId does not match a pending (unresponded,
  // non-deleted) reviewer on the current stage.
  REVIEW_REVIEWER_NOT_FOUND: {
    errorCode: 19012,
    errorMessage: t("The user can not be found"),
    isDynamic: false,
  },
  // `notifyUserInReview`: this reviewer was already notified within the last 2 hours.
  REVIEW_NOTIFICATION_RATE_LIMITED: {
    errorCode: 19013,
    errorMessage: t("The user can not be notified again as a recent notification was sent"),
    isDynamic: false,
  },
  // `changeEstimateStatus`-adjacent: the estimate's latest version is still a draft (no
  // `versionNumber`) — status can only be set on a published version.
  ESTIMATE_VERSION_NOT_PUBLISHED: {
    errorCode: 19014,
    errorMessage: t("Status can only be updated for published estimate versions"),
    isDynamic: false,
  },
  // `changeTaskStatus`-adjacent: the task has no `spaceId`, so the space-scoped default review flow
  // it would sync status through cannot be resolved.
  TASK_NOT_ATTACHED_TO_SPACE: {
    errorCode: 19015,
    errorMessage: t("Task is not attached to a Space"),
    isDynamic: false,
  },
  // `calculateApprovalPermissions`-adjacent: more than one approver is still required at this level,
  // so a single response is not enough to clear it.
  REVIEW_LEVEL_PENDING_RESPONSES: {
    errorCode: 19016,
    errorMessage: t("This review level still needs responses from other reviewers."),
    isDynamic: false,
  },
  // Continuing the 19xxx Reviews range into reviewflows.service.ts — same conceptual module
  // (review-flow templates rather than live review instances), split off SOMETHING_WENT_WRONG.
  //
  // The named reviewFlow is already attached to a live review, so its structure (initiators,
  // levels, reviewers, transitions) is frozen. Sent from the create/update/delete-adjacent write paths.
  REVIEW_FLOW_ALREADY_ATTACHED: {
    errorCode: 19017,
    errorMessage: t("The given review flow is already attached to a review"),
    isDynamic: false,
  },
  // The named reviewFlow TEMPLATE id does not resolve. Not REVIEW_FLOW_SPACE_NOT_FOUND (19002), which
  // createReview (reviews.service.ts) sends when the flow or its space is missing, nor REVIEW_NOT_FOUND
  // (19001, a review instance).
  REVIEW_FLOW_NOT_FOUND: {
    errorCode: 19018,
    errorMessage: t("The review flow does not exist"),
    isDynamic: false,
  },
  // `addInitiatorToReviewFlow`: every requested user id is already an initiator on this flow.
  REVIEW_USERS_ALREADY_INITIATORS: {
    errorCode: 19019,
    errorMessage: t("The selected user(s) are already a part of the list of initiators"),
    isDynamic: false,
  },
  // `removeInitiatorFromReviewFlow`: the named user id is not currently an initiator on this flow.
  REVIEW_INITIATOR_NOT_IN_FLOW: {
    errorCode: 19020,
    errorMessage: t("The selected initiator is not part of this review flow"),
    isDynamic: false,
  },
  // The named review-level id does not resolve on this reviewFlow. 3 call sites (2 share the
  // pre-existing `REVIEW_LEVEL_NOT_FOUND_MSG` constant, 1 has its own wording).
  REVIEW_LEVEL_NOT_FOUND: {
    errorCode: 19021,
    errorMessage: t("The review flow / review level could not be found"),
    isDynamic: false,
  },
  // `removeReviewLevel`: a reviewFlow must keep at least one level — the target is the only one left.
  REVIEW_LAST_LEVEL_UNDELETABLE: {
    errorCode: 19022,
    errorMessage: t("The last level can not be deleted"),
    isDynamic: false,
  },
  // `removeReviewLevel`: the level is referenced by one or more transitions and cannot be deleted
  // while those still point at it.
  REVIEW_LEVEL_IN_USE_BY_TRANSITION: {
    errorCode: 19023,
    errorMessage: t("The level is selected in transition(s)"),
    isDynamic: false,
  },
  // `addReviewerToLevel`: every requested user id is already a reviewer on this level.
  REVIEW_USERS_ALREADY_IN_LEVEL: {
    errorCode: 19024,
    errorMessage: t("The selected user(s) are already a part of this review level"),
    isDynamic: false,
  },
  // `removeReviewerFromLevel`: the level has exactly one reviewer left — a level must keep at least
  // one.
  REVIEW_LAST_REVIEWER_UNDELETABLE: {
    errorCode: 19025,
    errorMessage: t("The last reviewer can not be deleted"),
    isDynamic: false,
  },
  // `removeReviewerFromLevel`: the named user id is not currently a reviewer on this level.
  REVIEW_REVIEWER_NOT_IN_LEVEL: {
    errorCode: 19026,
    errorMessage: t("The selected reviewer is not part of this level"),
    isDynamic: false,
  },
  // `removeReviewerFromLevel`: removing this reviewer would leave a transition pointing at a
  // reviewer no longer on the level, corrupting it.
  REVIEW_REVIEWER_DELETE_BLOCKED_BY_TRANSITION: {
    errorCode: 19027,
    errorMessage: t("Deleting the reviewer from this level will corrupt the transitions made"),
    isDynamic: false,
  },
  // `getStatusDetailsFromReviewFlow`: the reviewFlow's resource-type status list is empty.
  REVIEW_FLOW_NO_STATUSES_CONFIGURED: {
    errorCode: 19028,
    errorMessage: t("Please configure statuses first"),
    isDynamic: false,
  },
  // `getStatusDetailsFromReviewFlow`: the requested status id is not one of the reviewFlow's
  // configured statuses. Also `updateEntityStatus` (reviews.service.ts): a status the space's default
  // review flow does not offer, on /changeEstimateStatus, /changeIssueStatus and /changeTaskStatus.
  REVIEW_FLOW_STATUS_NOT_CONFIGURED: {
    errorCode: 19029,
    errorMessage: t("The chosen status has not been added to review flow"),
    isDynamic: false,
  },
  // `getStatusDetailsFromReviewFlow`: the sentinel "Any" status (`_id: "0"`) was named as a
  // transition's TARGET status, which is not a real status to transition into.
  REVIEW_FLOW_ANY_STATUS_NOT_ALLOWED_AS_TARGET: {
    errorCode: 19030,
    errorMessage: t("The status Any can not be used in the target status"),
    isDynamic: false,
  },
  // `getTransition`: the named transition id does not resolve on the named reviewFlow.
  REVIEW_TRANSITION_NOT_FOUND: {
    errorCode: 19031,
    errorMessage: t("The review flow / transition could not be found"),
    isDynamic: false,
  },
  // 19032-19041 completes the reviews and reviewflows split (both share the 19xxx series).
  // REVIEW_DELETE_OPERATION_FAILED deliberately does not reuse SCHEDULE_DELETE_FAILED (10033),
  // which carries the same sentence for a different module. Six further sites reused
  // REVIEW_NOT_FOUND and one SPACE_NOT_FOUND, matching decisions made for those exact strings
  // earlier in this migration.
  REVIEW_FLOW_UPDATE_FAILED: {
    errorCode: 19032,
    errorMessage: t("The review flow could not be updated"),
    isDynamic: false,
  },
  REVIEW_FLOW_INVALID_REQUEST_FORMAT: {
    errorCode: 19033,
    errorMessage: t("Invalid request format"),
    isDynamic: false,
  },
  REVIEW_LEVEL_KEY_AND_FLOW_ID_REQUIRED: {
    errorCode: 19034,
    errorMessage: t("key (the review-level _id) and params.reviewFlowId are both required"),
    isDynamic: false,
  },
  REVIEW_TRANSITION_KEY_AND_FLOW_ID_REQUIRED: {
    errorCode: 19035,
    errorMessage: t("key (the transition _id) and params.reviewFlowId are both required"),
    isDynamic: false,
  },
  REVIEW_INITIATE_FAILED: {
    errorCode: 19036,
    errorMessage: t("The review could not be initiated"),
    isDynamic: false,
  },
  REVIEW_INTERNAL_SERVER_ERROR: {
    errorCode: 19037,
    errorMessage: t("Internal Server Error"),
    isDynamic: false,
  },
  REVIEW_UPDATE_FAILED: {
    errorCode: 19038,
    errorMessage: t("The review could not be updated"),
    isDynamic: false,
  },
  REVIEW_UPDATE_OPERATION_FAILED: {
    errorCode: 19039,
    errorMessage: t("Update operation failed"),
    isDynamic: false,
  },
  REVIEW_DELETE_OPERATION_FAILED: {
    errorCode: 19040,
    errorMessage: t("Delete operation failed"),
    isDynamic: false,
  },
  REVIEW_QUESTION_NOT_FOUND: {
    errorCode: 19041,
    errorMessage: t("Question ID not found"),
    isDynamic: false,
  },
  REVIEW_MARK_NO_PERMISSION: {
    errorCode: 19042,
    errorMessage: t("User does not have permissions to mark the review"),
    isDynamic: false,
  },
  REVIEW_SKIP_NOT_ALLOWED: {
    errorCode: 19043,
    errorMessage: t("Skip Operation not allowed"),
    isDynamic: false,
  },
  // 19044-19046 came off NO_CHANGES_APPLIED (9005).
  REVIEW_FILE_VERSION_ALREADY_ADDED: {
    errorCode: 19044,
    errorMessage: t("The latest version of the selected file(s) is already added in the review"),
    isDynamic: false,
  },
  REVIEW_ALREADY_MARKED: {
    errorCode: 19045,
    errorMessage: t("User has already marked the review"),
    isDynamic: false,
  },
  REVIEW_ALREADY_CLOSED: {
    errorCode: 19046,
    errorMessage: t("The review has been closed already"),
    isDynamic: false,
  },
  REVIEW_ROLLBACK_NOT_ALLOWED: {
    errorCode: 19051,
    errorMessage: t("The review can not be rolled back"),
    isDynamic: false,
  },
  REVIEW_FLOW_NOT_FOUND_BY_ID: {
    errorCode: 19047,
    errorMessage: t("The given review flow with ID {{reviewFlowId}} does not exist"),
    isDynamic: true,
    dynamicParams: ["reviewFlowId"],
  },
  REVIEW_FLOW_MISSING_SPACE: {
    errorCode: 19048,
    errorMessage: t("The given review flow with ID {{reviewFlowId}} is missing a space"),
    isDynamic: true,
    dynamicParams: ["reviewFlowId"],
  },
  REVIEW_FLOW_MISSING_PORTAL: {
    errorCode: 19049,
    errorMessage: t("The given review flow with ID {{reviewFlowId}} is missing a portal"),
    isDynamic: true,
    dynamicParams: ["reviewFlowId"],
  },
  REVIEW_FLOW_MISSING_PROJECT: {
    errorCode: 19050,
    errorMessage: t("The given review flow with ID {{reviewFlowId}} is missing a project"),
    isDynamic: true,
    dynamicParams: ["reviewFlowId"],
  },
  // 19052-19056: the review-comment sub-routes, which reported through a bare errorMessage.
  REVIEW_NOT_FOUND_PLAIN: {
    errorCode: 19052,
    errorMessage: t("Review not found"),
    isDynamic: false,
  },
  REVIEW_COMMENT_SAVE_FAILED: {
    errorCode: 19053,
    errorMessage: t("Failed to save comment"),
    isDynamic: false,
  },
  REVIEW_COMMENT_UPDATE_FAILED: {
    errorCode: 19054,
    errorMessage: t("Failed to update comment"),
    isDynamic: false,
  },
  REVIEW_COMMENT_ID_REQUIRED: {
    errorCode: 19055,
    errorMessage: t("Comment ID is required"),
    isDynamic: false,
  },
  REVIEW_COMMENT_DELETE_FAILED: {
    errorCode: 19056,
    errorMessage: t("Failed to delete comment"),
    isDynamic: false,
  },
  // 19057-19058: the two review-stage refusals that had no code; Revoked already had 19007.
  REVIEW_NOT_INITIATED: {
    errorCode: 19057,
    errorMessage: t("The review has not been initiated yet"),
    isDynamic: false,
  },
  REVIEW_CLOSED: {
    errorCode: 19058,
    errorMessage: t("The review has been closed"),
    isDynamic: false,
  },
  // 19059-19063: reviewflows transition validation, which returned sentences from a local helper.
  REVIEW_FLOW_STATUSES_NOT_CONFIGURED: {
    errorCode: 19059,
    errorMessage: t("The statuses have not been configured correctly"),
    isDynamic: false,
  },
  REVIEW_FLOW_STATUSES_SAME: {
    errorCode: 19060,
    errorMessage: t("The statuses selected can not be same"),
    isDynamic: false,
  },
  REVIEW_FLOW_LEVEL_NEEDS_REVIEWER: {
    errorCode: 19061,
    errorMessage: t("Please select atleast one reviewer in a level"),
    isDynamic: false,
  },
  REVIEW_FLOW_TRANSITION_DUPLICATE: {
    errorCode: 19062,
    errorMessage: t("Transition with same statuses already exists"),
    isDynamic: false,
  },
  REVIEW_FLOW_TRANSITION_SAVE_FAILED: {
    errorCode: 19063,
    errorMessage: t("The transition could not be saved"),
    isDynamic: false,
  },
  REVIEW_CHECKLIST_TITLE_AND_MULTIPLE_REQUIRED: {
    errorCode: 19064,
    errorMessage: t("A new checklist needs a title and allowMultiple"),
    isDynamic: false,
  },
  REVIEW_FILE_AND_NAME_REQUIRED: {
    errorCode: 19065,
    errorMessage: t("A file and its name are required"),
    isDynamic: false,
  },
  REVIEW_USER_NOT_INITIATOR: {
    errorCode: 19066,
    errorMessage: t("Only initiators can initiate the review"),
    isDynamic: false,
  },
  REVIEW_DRAFT_INITIATORS_ONLY: {
    errorCode: 19067,
    errorMessage: t("Only initiators can change the review before it is initiated"),
    isDynamic: false,
  },
} satisfies Record<string, ErrorCodeMapperEntry>;
